import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { OfficeModel, FurnitureKind } from './buildOfficeLayout';
import { prettyCargo } from '../pessoal/jobArchetypes';
import { IsoPerson } from './officePeople';
import {
  TW,
  TH,
  TD,
  WALL_H,
  drawTile,
  drawWalls,
  drawPerson,
  drawDesk,
  drawSewing,
  drawMachine,
  drawCounter,
  drawStove,
  drawShelf,
  drawMaca,
  drawCutTable,
  drawPlant,
  drawBubble,
} from './pixelSprites';
import { furniForArch } from './officeTheme';

const SCALE = 2;
const HAIR = ['#3B2B26', '#6B4A2F', '#1F1A17', '#4A3B33', '#7A5230'];
const FLOOR_A = '#E7EFE4';
const FLOOR_B = '#DDE8D9';
const WALK_CAP = 6; // máximo de andarilhos ao mesmo tempo
const BUBBLE_CAP = 4; // máximo de balões ao mesmo tempo
const EMOJI = ['😊', '☕', '💭', '🙂', '✅', '💡', '👍'];
const EMOJI_ARCH: Record<string, string> = {
  textil: '🧵',
  ti: '💻',
  cozinha: '🍳',
  bar: '🍹',
  vendas: '🛍️',
  caixa: '🧾',
  saude: '🩺',
  lideranca: '📊',
  financeiro: '💰',
  rh: '🤝',
  logistica: '📦',
  construcao: '🔨',
  seguranca: '🛡️',
};
const pickEmoji = (key: string) => {
  const special = EMOJI_ARCH[key];
  if (special && Math.random() < 0.45) return special;
  return EMOJI[Math.floor(Math.random() * EMOJI.length)];
};

const hairFor = (id: string) => {
  let s = 0;
  for (let i = 0; i < id.length; i += 1) s += id.charCodeAt(i);
  return HAIR[s % HAIR.length];
};
const phaseFor = (id: string) => {
  let s = 0;
  for (let i = 0; i < id.length; i += 1) s += id.charCodeAt(i) * (i + 1);
  return (s % 1000) / 1000;
};

interface Agent {
  person: IsoPerson;
  homeC: number;
  homeR: number;
  c: number;
  r: number;
  ph: number;
  walking: boolean;
  tc: number;
  tr: number;
  returning: boolean;
  kind: FurnitureKind;
  emoji: string;
  bubbleUntil: number;
}
interface HitBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  label: string;
}

/** Renderer canvas pixel-art (estilo Habbo) com vida: trabalham + andam (rAF único). */
export function IsoCanvas({ model }: { model: OfficeModel }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const hitsRef = useRef<HitBox[]>([]);
  const [tip, setTip] = useState<{ x: number; y: number; label: string } | null>(null);

  const dims = useMemo(() => {
    const PAD_X = TW / 2 + 8;
    const PAD_TOP = WALL_H + 22; // espaço pras paredes do fundo
    const PAD_BOT = TH + TD + 8;
    const offX = model.rows * (TW / 2) + PAD_X;
    const offY = PAD_TOP;
    const W = (model.cols + model.rows) * (TW / 2) + 2 * PAD_X;
    const H = (model.cols + model.rows) * (TH / 2) + PAD_TOP + PAD_BOT;
    return { offX, offY, W, H };
  }, [model]);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    if (!ctx) return undefined;
    ctx.imageSmoothingEnabled = false;

    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const top = (c: number, r: number) => ({
      x: dims.offX + (c - r) * (TW / 2),
      y: dims.offY + (c + r) * (TH / 2),
    });
    const roomAt = (c: number, r: number) =>
      model.rooms.find(rm => c >= rm.c0 && c < rm.c0 + rm.w && r >= rm.r0 && r < rm.r0 + rm.h);

    const agents: Agent[] = model.stations.map(s => ({
      person: s.person,
      homeC: s.c - 0.16,
      homeR: s.r - 0.16,
      c: s.c - 0.16,
      r: s.r - 0.16,
      ph: phaseFor(s.person.id) * Math.PI * 2,
      walking: false,
      tc: 0,
      tr: 0,
      returning: false,
      kind: furniForArch(s.person.archetypeKey),
      emoji: '',
      bubbleUntil: 0,
    }));

    // tiles do piso (estático)
    const tiles: { c: number; r: number }[] = [];
    for (let r = 0; r < model.rows; r += 1) {
      for (let c = 0; c < model.cols; c += 1) tiles.push({ c, r });
    }

    let raf = 0;
    let last = 0;
    let walkTimer = 0.8;
    let walkers = 0;
    let bubbleTimer = 1.5;

    const frame = (ts: number) => {
      const t = ts / 1000;
      const dt = last ? Math.min(0.05, t - last) : 0;
      last = t;

      // diretor: solta andarilhos
      if (!reduce) {
        walkTimer -= dt;
        if (walkTimer <= 0) {
          walkTimer = 1 + Math.random() * 1.6;
          if (walkers < WALK_CAP) {
            const idle = agents.filter(a => !a.walking);
            if (idle.length) {
              const a = idle[Math.floor(Math.random() * idle.length)];
              a.walking = true;
              a.returning = false;
              a.tc = 0.5 + Math.random() * Math.max(1, model.cols - 1.5);
              a.tr = 0.5 + Math.random() * Math.max(1, model.rows - 1.5);
              walkers += 1;
            }
          }
        }
        // atualiza posições
        for (let i = 0; i < agents.length; i += 1) {
          const a = agents[i];
          if (a.walking) {
            const dx = a.tc - a.c;
            const dy = a.tr - a.r;
            const dist = Math.hypot(dx, dy);
            const step = 1.7 * dt;
            if (dist <= step) {
              a.c = a.tc;
              a.r = a.tr;
              if (!a.returning) {
                a.returning = true;
                a.tc = a.homeC;
                a.tr = a.homeR;
              } else {
                a.walking = false;
                a.c = a.homeC;
                a.r = a.homeR;
                walkers -= 1;
              }
            } else {
              a.c += (dx / dist) * step;
              a.r += (dy / dist) * step;
            }
          }
        }
        // diretor: solta balões de emoji
        bubbleTimer -= dt;
        if (bubbleTimer <= 0) {
          bubbleTimer = 0.8 + Math.random() * 1.4;
          const active = agents.filter(a => a.bubbleUntil > t).length;
          if (active < BUBBLE_CAP) {
            const free = agents.filter(a => a.bubbleUntil <= t && !a.walking);
            if (free.length) {
              const a = free[Math.floor(Math.random() * free.length)];
              a.emoji = pickEmoji(a.person.archetypeKey);
              a.bubbleUntil = t + 2.5;
            }
          }
        }
      }

      // desenha
      ctx.clearRect(0, 0, dims.W, dims.H);
      const wa = top(0, 0);
      const wb = top(model.cols, 0);
      const wc = top(0, model.rows);
      drawWalls(ctx, wa.x, wa.y, wb.x, wb.y, wc.x, wc.y);
      tiles.forEach(({ c, r }) => {
        const p = top(c, r);
        const chk = (c + r) % 2 === 0;
        drawTile(ctx, p.x, p.y, chk ? FLOOR_A : FLOOR_B, '#C7D3C4', '#BCC9BA');
        const room = roomAt(c, r);
        if (room) {
          ctx.fillStyle = `${room.color}40`; // zona do departamento (cor da função)
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + TW / 2, p.y + TH / 2);
          ctx.lineTo(p.x, p.y + TH);
          ctx.lineTo(p.x - TW / 2, p.y + TH / 2);
          ctx.closePath();
          ctx.fill();
        }
      });

      type Obj = { c: number; r: number; fn: () => void };
      const objs: Obj[] = [];
      model.props.forEach(pr => {
        objs.push({
          c: pr.c,
          r: pr.r,
          fn: () => {
            const p = top(pr.c, pr.r);
            if (pr.kind === 'cuttable') drawCutTable(ctx, p.x, p.y + TH / 2);
            else drawPlant(ctx, p.x, p.y + TH / 2);
          },
        });
      });
      const hits: HitBox[] = [];
      for (let i = 0; i < agents.length; i += 1) {
        const a = agents[i];
        // mesa fica no posto (estática); pessoa pode andar
        objs.push({
          c: a.homeC + 0.16,
          r: a.homeR + 0.16,
          fn: () => {
            const p = top(a.homeC + 0.16, a.homeR + 0.16);
            const dx = p.x;
            const dy = p.y + TH / 2;
            if (a.kind === 'sewing') drawSewing(ctx, dx, dy, a.person.color);
            else if (a.kind === 'machine') drawMachine(ctx, dx, dy, a.person.color);
            else if (a.kind === 'counter') drawCounter(ctx, dx, dy, a.person.color);
            else if (a.kind === 'stove') drawStove(ctx, dx, dy);
            else if (a.kind === 'shelf') drawShelf(ctx, dx, dy);
            else if (a.kind === 'maca') drawMaca(ctx, dx, dy);
            else drawDesk(ctx, dx, dy, a.person.color);
          },
        });
        const bob = a.walking
          ? Math.round(Math.abs(Math.sin(t * 9 + a.ph)) * 2)
          : Math.round(Math.max(0, Math.sin(t * 2.2 + a.ph)) * 1.3);
        objs.push({
          c: a.c,
          r: a.r,
          fn: () => {
            const p = top(a.c, a.r);
            const ox = p.x;
            const oy = p.y + TH / 2 - bob;
            drawPerson(ctx, ox, oy, {
              skin: a.person.skin,
              shirt: a.person.color,
              hair: hairFor(a.person.id),
              fem: a.person.gender === 'F',
            });
            hits.push({
              x0: ox - 9,
              y0: oy - 40,
              x1: ox + 9,
              y1: oy + 2,
              label: a.person.nome ?? prettyCargo(a.person.cargo),
            });
          },
        });
      }
      objs.sort((p, q) => p.c + p.r - (q.c + q.r));
      objs.forEach(o => o.fn());
      hitsRef.current = hits;

      // balões de emoji (camada de cima)
      for (let i = 0; i < agents.length; i += 1) {
        const a = agents[i];
        if (a.bubbleUntil > t) {
          const p = top(a.c, a.r);
          drawBubble(ctx, p.x, p.y + TH / 2, a.emoji);
        }
      }

      raf = window.requestAnimationFrame(frame);
    };
    raf = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(raf);
  }, [model, dims]);

  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const cv = ref.current;
    if (!cv) return;
    const rectb = cv.getBoundingClientRect();
    const mx = (e.clientX - rectb.left) / SCALE;
    const my = (e.clientY - rectb.top) / SCALE;
    const hit = [...hitsRef.current]
      .reverse()
      .find(h => mx >= h.x0 && mx <= h.x1 && my >= h.y0 && my <= h.y1);
    if (hit) setTip({ x: (hit.x0 + hit.x1) / 2, y: hit.y0, label: hit.label });
    else setTip(null);
  };

  return (
    <Box position="relative" w={`${dims.W * SCALE}px`} h={`${dims.H * SCALE}px`} mx="auto">
      <canvas
        ref={ref}
        width={dims.W}
        height={dims.H}
        onMouseMove={onMove}
        onMouseLeave={() => setTip(null)}
        style={{
          width: dims.W * SCALE,
          height: dims.H * SCALE,
          imageRendering: 'pixelated',
          display: 'block',
        }}
      />
      {tip && (
        <Box
          position="absolute"
          left={`${tip.x * SCALE}px`}
          top={`${tip.y * SCALE - 26}px`}
          transform="translateX(-50%)"
          bg="rgba(15,23,42,0.92)"
          color="white"
          px={2}
          py={0.5}
          borderRadius="6px"
          fontSize="11px"
          fontWeight={700}
          whiteSpace="nowrap"
          pointerEvents="none"
          zIndex={5}
        >
          <Text>{tip.label}</Text>
        </Box>
      )}
    </Box>
  );
}
